import {CalloutIcon} from '@wandb/components';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {useHistory} from 'react-router';

import {
  useNavContext,
  useNavUpdaterContext,
} from '../../../components/NavContextProvider';
import {useViewer} from '../../../state/viewer/hooks';
import {useRampFlagStarredProjects} from '../../../util/rampFeatureFlags';
import {maybePluralizeWord} from '../../../util/uihelpers';
import * as urls from '../../../util/urls';
import CreateProjectDrawer from '../HomePageSidebar/CreateProjectDrawer';
import * as S from '../HomePageWithData.styles';
import {MimicProjectCards} from './MimicProjectCards';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ProjectCards} from './ProjectCards';
import {
  type AccountProjectData,
  getHomePageProject,
  type HomePageProject,
} from './util';

type ProjectSectionsProps = {
  starredProjects: HomePageProject[];
  recentProjects: HomePageProject[];
  isLoading: boolean;
};

type StarredProjectSectionProps = {
  starredProjects: HomePageProject[];
  isLoading: boolean;
};

type RecentProjectSectionProps = {
  recentProjects: HomePageProject[];
  isLoading: boolean;
};

export type GetHomePageProjectsProps = {
  projects: AccountProjectData[];
  starredProjectIDs?: Set<string>;
  numProjects: number;
};

export function getHomePageProjects({
  projects,
  starredProjectIDs,
  numProjects,
}: GetHomePageProjectsProps): HomePageProject[] {
  const homePageProjects: HomePageProject[] = [];
  for (
    let i = 0;
    i < projects.length && homePageProjects.length < numProjects;
    i++
  ) {
    const p = projects[i];
    if (!p) {
      continue;
    }
    if (starredProjectIDs?.has(p.id)) {
      // Exclude starred projects because they're shown in a separate section.
      continue;
    }
    homePageProjects.push(getHomePageProject(p));
  }
  return homePageProjects;
}

export const EmptyStarredProjectsCard: React.FC = () => (
  <Tailwind>
    <div className="mb-16 flex h-full flex-col items-center justify-center rounded-lg border border-moon-250 bg-white p-24">
      <CalloutIcon icon="star" color="gold" />
      <div className="text-lg font-semibold">No starred projects</div>
      <div className="text-gray-500">
        Star a project from your projects list and it will always show here.
      </div>
    </div>
  </Tailwind>
);

export const StarredProjectSection: React.FC<StarredProjectSectionProps> = ({
  starredProjects,
  isLoading,
}) => {
  const projectText = maybePluralizeWord(starredProjects?.length, 'project');
  return (
    <div data-test="starred-projects">
      <S.HomePageSectionHeader>
        {'Starred ' + projectText}
      </S.HomePageSectionHeader>
      {isLoading ? (
        <MimicProjectCards numCards={4} />
      ) : starredProjects?.length > 0 ? (
        <ProjectCards projects={starredProjects} />
      ) : (
        <EmptyStarredProjectsCard />
      )}
    </div>
  );
};
StarredProjectSection.displayName = 'StarredProjectSection';

export const RecentProjectSection: React.FC<RecentProjectSectionProps> = ({
  recentProjects,
  isLoading,
}) => {
  const history = useHistory();
  const viewer = useViewer();
  const recentProjectText = maybePluralizeWord(
    recentProjects?.length,
    'project'
  );

  const {isCreatingProject} = useNavContext();
  const {toggleCreateProjectDrawer} = useNavUpdaterContext();
  return isLoading ? (
    <S.HomePageSectionHeader>
      {'Your recent ' + recentProjectText}
      <MimicProjectCards numCards={4} />
    </S.HomePageSectionHeader>
  ) : recentProjects.length > 0 ? (
    <div data-test="recent-projects">
      <S.HomePageHeader>
        <S.HomePageSectionHeader>
          {'Your recent ' + recentProjectText}
        </S.HomePageSectionHeader>
        <S.HomePageButton>
          <Button
            variant="ghost"
            endIcon="chevron-next"
            onClick={() =>
              history.push(urls.allProjects(viewer?.username ?? ''))
            }>
            View all
          </Button>
          <Button startIcon="add-new" onClick={toggleCreateProjectDrawer}>
            New project
          </Button>
          <CreateProjectDrawer
            open={isCreatingProject}
            toggleProjectDrawer={toggleCreateProjectDrawer}
          />
        </S.HomePageButton>
      </S.HomePageHeader>
      <ProjectCards projects={recentProjects} />
    </div>
  ) : null;
};

export const ProjectSections: React.FC<ProjectSectionsProps> = React.memo(
  ({starredProjects, recentProjects, isLoading}) => {
    const isStarredProjectsEnabled = useRampFlagStarredProjects();

    return (
      <>
        {isStarredProjectsEnabled && (
          <StarredProjectSection
            starredProjects={starredProjects}
            isLoading={isLoading}
          />
        )}
        <RecentProjectSection
          recentProjects={recentProjects}
          isLoading={isLoading}
        />
      </>
    );
  }
);
ProjectSections.displayName = 'ProjectSections';
